import React from "react";
import PropTypes from "prop-types";
import BaseBlock from "./BaseBlock";
import useBlockData from "./useBlockData";
import FlexTable from "../../../components/ui-components/FlexTable";
import { fmtEur, fmtEurSigned } from "./format";

// TONE_CLASS mirrors KPIStripBlock's mapping of the adverse/favorable/neutral vocabulary
// (see backend_go internal/boardfigures/kpi.go's varianceTone) to a colour class. Reused
// here directly since /figures/performance rows carry a raw Variance number rather than a
// pre-computed tone field, so this block derives it the same way the backend's KPI strip
// does: over budget (positive) is adverse, under budget (negative) is favorable.
const TONE_CLASS = {
  adverse: "a4-kpi-tone-adverse",
  favorable: "a4-kpi-tone-favorable",
  neutral: "a4-kpi-tone-neutral",
};

function varianceTone(variance) {
  if (variance > 0) return "adverse";
  if (variance < 0) return "favorable";
  return "neutral";
}

function isTotalRow(row) {
  return row.original.metric === "Total";
}

// emphasise wraps a cell's rendered content in a bold span for the reconciling Total row,
// so it visually stands apart from the Cloud/AI split rows above it.
function emphasise(bold, node, extraClassName) {
  const className = [bold ? "font-semibold" : null, extraClassName].filter(Boolean).join(" ") || undefined;
  return <span className={className}>{node}</span>;
}

const columns = [
  { Header: "Metric", accessor: "metric", Cell: ({ value, row }) => emphasise(isTotalRow(row), value) },
  {
    Header: "Month",
    accessor: "month",
    align: "right",
    Cell: ({ value, row }) => emphasise(isTotalRow(row), fmtEur(value)),
  },
  {
    Header: "Budget",
    accessor: "budget",
    align: "right",
    Cell: ({ value, row }) => emphasise(isTotalRow(row), fmtEur(value)),
  },
  {
    Header: "Variance",
    accessor: "variance",
    align: "right",
    Cell: ({ value, row }) => emphasise(isTotalRow(row), fmtEurSigned(value), TONE_CLASS[varianceTone(value)]),
  },
  {
    Header: "YTD",
    accessor: "ytd",
    align: "right",
    Cell: ({ value, row }) => emphasise(isTotalRow(row), fmtEur(value)),
  },
  {
    Header: "YTD Budget",
    accessor: "ytd_budget",
    align: "right",
    Cell: ({ value, row }) => emphasise(isTotalRow(row), fmtEur(value)),
  },
  {
    Header: "FY Forecast",
    accessor: "fy_forecast",
    align: "right",
    Cell: ({ value, row }) => emphasise(isTotalRow(row), fmtEur(value)),
  },
];

/**
 * PerformanceTableBlock - Cloud / AI / Total performance table (performance_table block
 * type): this month's actual/budget/variance plus YTD and full-year-forecast per split.
 *
 * GET /api/v1/board-reports/{boardReportId}/runs/{runId}/figures/performance -> envelope
 * key "performance" -> boardfigures.PerformanceTable { rows: [PerfRow] } (internal/
 * boardfigures/performance.go's BuildPerformance) - exactly 3 rows, in order Cloud, AI,
 * Total, with Total = Cloud + AI on every column by construction.
 */
const PerformanceTableBlock = ({ title = "Performance", boardReportId, runId }) => {
  const { data, isLoading, error } = useBlockData(
    `/api/v1/board-reports/${boardReportId}/runs/${runId}/figures/performance`
  );

  const rows = data?.performance?.rows ?? [];

  return (
    <BaseBlock title={title} isLoading={isLoading} error={error} className="a4-performance-table">
      <FlexTable columns={columns} data={rows} getRowId={(row) => row.metric} />
    </BaseBlock>
  );
};

PerformanceTableBlock.propTypes = {
  title: PropTypes.string,
  boardReportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  runId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default PerformanceTableBlock;
